/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { type FC, type PropsWithChildren, Suspense } from 'react';

import { GlobalLayout } from '@coze-foundation/layout';
import { RequireAuthContainer } from '@coze-foundation/account-ui-adapter';
import { I18n } from '@coze-arch/i18n';
import {
  IconCozPlugin,
  IconCozPluginFill,
  IconCozTemplate,
  IconCozTemplateFill,
  IconCozDocument,
} from '@coze-arch/coze-design/icons';
import { Spin } from '@coze-arch/coze-design';
import { useRouteConfig } from '@coze-arch/bot-hooks';

import { useHasSider } from './hooks/use-has-sider';
import { AppVersion } from '../app-version';
import { AccountDropdown } from '../account-dropdown';

export const GlobalLayoutComposed: FC<PropsWithChildren> = ({ children }) => {
  const config = useRouteConfig();
  const hasSider = useHasSider();

  return (
    <RequireAuthContainer
      needLogin={!!config.requireAuth}
      loginOptional={!!config.requireAuthOptional}
    >
      <GlobalLayout
        hasSider={hasSider}
        banner={null}
        menus={[
          {
            title: I18n.t('Plugins'),
            icon: <IconCozPlugin />,
            activeIcon: <IconCozPluginFill />,
            path: '/explore/plugin',
            dataTestId: 'layout_explore-plugin-button',
          },
          {
            title: I18n.t('template_name'),
            icon: <IconCozTemplate />,
            activeIcon: <IconCozTemplateFill />,
            path: '/explore/template',
            dataTestId: 'layout_explore-template-button',
          },
        ]}
        extras={[
          {
            icon: <IconCozDocument />,
            tooltip: I18n.t('menu_documents'),
            onClick: () => {
              // cp-disable-next-line
              window.open(
                (I18n.language || '').toLowerCase().startsWith('en')
                  ? 'https://docs.coze.com/guides/welcome'
                  : 'https://www.coze.cn/open/docs/guides',
              );
            },
            dataTestId: 'layout_document-button',
          },
        ]}
        footer={
          <div>
            <AccountDropdown showUserName />
            <AppVersion />
          </div>
        }
      >
        <Suspense
          fallback={
            <div className="flex h-full w-full items-center justify-center">
              <Spin spinning />
            </div>
          }
        >
          {children}
        </Suspense>
      </GlobalLayout>
    </RequireAuthContainer>
  );
};
